import { Autocomplete, CircularProgress, TextField } from "@mui/material";
import { Package } from "lucide-react";
import { dictionary } from "../../utils/dictionary";
import { useAuth } from "../../context/AuthContext";

interface Product {
  id: string;
  name: string;
  price: number;
  images?: string[];
  description?: string;
}

interface ProductFilterProps {
  products: Product[];
  isLoading?: boolean;
  onProductSelect: (productId: string) => void;
}

export const ProductFilter: React.FC<ProductFilterProps> = ({
  products,
  isLoading = false,
  onProductSelect,
}) => {
  const { language } = useAuth();
  
  const safeProducts = Array.isArray(products) ? products : [];
  
  const getNoOptionsText = () => {
    if (isLoading) {
      return dictionary["loading-products"]?.[language] || "Cargando productos...";
    }
    
    if (safeProducts.length === 0) {
      return dictionary["no-products-found"]?.[language] || "No se encontraron productos";
    }
    
    return dictionary["no-products-found"]?.[language] || "No se encontraron productos";
  };
  
  const getPlaceholder = () => {
    if (isLoading) {
      return dictionary["loading-products"]?.[language] || "Cargando...";
    }
    
    return dictionary["search-product"]?.[language] || "Buscar producto...";
  };
  
  return (
    <div className="w-full max-w-md">
      <Autocomplete
        id="product-filter-autocomplete"
        options={safeProducts}
        loading={isLoading}
        getOptionLabel={(option) => option?.name || ''}
        isOptionEqualToValue={(option, value) => option.id === value.id}
        onChange={(_, product) => {
          if (product) onProductSelect(product.id);
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            placeholder={getPlaceholder()}
            variant="outlined"
            fullWidth
            InputProps={{
              ...params.InputProps,
              startAdornment: (
                <>
                  <Package className="w-5 h-5 text-gray-500 mr-2" />
                  {params.InputProps.startAdornment}
                </>
              ),
              endAdornment: (
                <>
                  {isLoading ? (
                    <CircularProgress color="inherit" size={20} />
                  ) : null}
                  {params.InputProps.endAdornment}
                </>
              ),
              className: "border border-gray-100 rounded-xl bg-white",
              style: {
                color: 'black',
                WebkitTextFillColor: 'black',
              },
            }}
          />
        )}
        renderOption={(props: React.HTMLAttributes<HTMLLIElement> & { 'data-option-index'?: number }, option) => (
          <li {...props} key={option.id || `product-${props['data-option-index']}`} className="px-4 py-2 hover:bg-gray-100 cursor-pointer">
            <div className="flex items-center">
              {option.images && option.images[0] && (
                <img
                  src={option.images[0]}
                  alt={option.name}
                  className="w-10 h-10 object-cover rounded-md mr-3"
                />
              )}
              <div className="flex-1">
                <div className="font-medium text-sm">{option.name || 'Producto sin nombre'}</div>
                <div className="text-xs text-gray-500">
                  ${option.price?.toLocaleString('es-CO') || '0'}
                </div>
              </div>
            </div>
          </li>
        )}
        noOptionsText={getNoOptionsText()}
        loadingText={dictionary["loading-products"]?.[language] || "Cargando..."}
        className="w-full"
        sx={{
          '& .MuiOutlinedInput-root': {
            height: '48px',
            borderRadius: '0.75rem',
            padding: '0 14px',
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderColor: 'var(--fluxi-primary-hex, #4368E5)',
              borderWidth: '1px',
            },
          },
          '& .MuiAutocomplete-endAdornment': {
            right: '8px',
          },
          '& .MuiAutocomplete-inputRoot': {
            paddingTop: '0 !important',
            paddingBottom: '0 !important',
            display: 'flex',
            alignItems: 'center',
          },
          '& .MuiAutocomplete-input': {
            height: '40px',
            padding: '0 !important',
            color: 'black',
          }
        }}
      />
    </div>
  );
};

export default ProductFilter;

